import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import useOnboarding from '@hooks/useOnboarding';

const ReferralRedirect = () => {
  const { code } = useParams();
  return <Navigate to={`/register?ref=${code}`} replace />;
};

// Layouts
import MainLayout from '@components/layout/MainLayout';
import AuthLayout from '@components/layout/AuthLayout';
import LandingLayout from '@components/layout/LandingLayout';

// Pages - Public
import Landing from '@pages/public/Landing';
import Pricing from '@pages/public/Pricing';
import About from '@pages/public/About';
import Contact from '@pages/public/Contact';
import Blog from '@pages/public/Blog';
import BlogPost from '@pages/public/BlogPost';
import PublicHelp from '@pages/public/Help';
import Privacy from '@pages/public/Privacy';
import Terms from '@pages/public/Terms';
import PublicFeatures from '@pages/public/Features';

// Pages - Auth
import Login from '@pages/auth/Login';
import Register from '@pages/auth/Register';
import VerifyEmail from '@pages/auth/VerifyEmail';
import ForgotPassword from '@pages/auth/ForgotPassword';
import ResetPassword from '@pages/auth/ResetPassword';
import AcceptInvite from '@pages/auth/AcceptInvite';

// Pages - App
import Dashboard from '@pages/app/Dashboard';
import Onboarding from '@pages/app/Onboarding';
import Accounts from '@pages/app/Accounts';
import Transactions from '@pages/app/Transactions';
import Categories from '@pages/app/Categories';
import Recurrences from '@pages/app/Recurrences';
import Installments from '@pages/app/Installments';
import Reports from '@pages/app/Reports';
import Simulation from '@pages/app/Simulation';
import Investments from '@pages/app/Investments';
import Notifications from '@pages/app/Notifications';
import Help from '@pages/app/Help';
import Family from '@pages/app/Family';
import Goals from '@pages/app/Goals';
import Gamification from '@pages/app/Gamification';
import Referral from '@pages/app/Referral';
import Recommendations from '@pages/app/Recommendations';
import OpenFinance from '@pages/app/OpenFinance';
import Budget from '@pages/app/Budget';
import AccountDetail from '@pages/app/AccountDetail';

// Pages - Settings
import Settings from '@pages/settings/Settings';
import Profile from '@pages/settings/Profile';
import Security from '@pages/settings/Security';
import SettingsNotifications from '@pages/settings/Notifications';
import Data from '@pages/settings/Data';
import Billing from '@pages/app/settings/Billing';

// Pages - Admin
import AdminDashboard from '@pages/admin/AdminDashboard';
import AdminTenants from '@pages/admin/AdminTenants';
import AdminUsers from '@pages/admin/AdminUsers';
import AdminMetrics from '@pages/admin/AdminMetrics';
import AdminRevenue from '@pages/admin/AdminRevenue';
import AdminBroadcast from '@pages/admin/AdminBroadcast';
import AdminLayout from '@components/layout/AdminLayout';

// Pages - Error
import NotFound from '@pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

// Onboarding Guard - redirects to onboarding if not completed
const OnboardingGuard = ({ children }) => {
  const { isComplete } = useOnboarding();

  if (!isComplete) {
    return <Navigate to="/app/onboarding" replace />;
  }

  return children;
};

// Admin Route Guard - requires role === 'admin'
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Landing Pages */}
      <Route element={<LandingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/help" element={<PublicHelp />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/features" element={<PublicFeatures />} />
      </Route>

      {/* Referral redirect: /r/:code → /register?ref=CODE */}
      <Route path="/r/:code" element={<ReferralRedirect />} />

      {/* Auth Pages */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
      </Route>

      {/* Onboarding (Protected, no MainLayout) */}
      <Route
        path="/app/onboarding"
        element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
      />

      {/* App Pages (Protected + Onboarding Guard) */}
      <Route
        path="/app"
        element={<ProtectedRoute><OnboardingGuard><MainLayout /></OnboardingGuard></ProtectedRoute>}
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/:accountId" element={<AccountDetail />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="categories" element={<Categories />} />
        <Route path="recurrences" element={<Recurrences />} />
        <Route path="installments" element={<Installments />} />
        <Route path="investments" element={<Investments />} />
        <Route path="reports" element={<Reports />} />
        <Route path="simulation" element={<Simulation />} />
        <Route path="goals" element={<Goals />} />
        <Route path="family" element={<Family />} />
        <Route path="budget" element={<Budget />} />
        <Route path="advisor" element={<Recommendations />} />
        <Route path="gamification" element={<Gamification />} />
        <Route path="referral" element={<Referral />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="help" element={<Help />} />
        <Route path="open-finance" element={<OpenFinance />} />

        {/* Settings (layout com sidebar próprio) */}
        <Route path="settings" element={<Settings />}>
          <Route index element={<Navigate to="/app/settings/profile" replace />} />
          <Route path="profile" element={<Profile />} />
          <Route path="security" element={<Security />} />
          <Route path="notifications" element={<SettingsNotifications />} />
          <Route path="data" element={<Data />} />
          <Route path="billing" element={<Billing />} />
        </Route>
      </Route>

      {/* Admin Panel */}
      <Route
        path="/admin"
        element={<AdminRoute><AdminLayout /></AdminRoute>}
      >
        <Route index element={<AdminDashboard />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="metrics" element={<AdminMetrics />} />
        <Route path="revenue" element={<AdminRevenue />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
